"use client";

import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface GuestCheckoutIdleStepProps {
  amount: number;
  email: string;
  emailError: string | null;
  error: string | null;
  isStarting: boolean;
  startingLabel: string;
  onEmailChange: (value: string) => void;
  onSubmit: () => void;
}

/**
 * Idle step of the guest bundle checkout: price anchor plus mandatory email
 * capture. The email receives the receipt and the cross-device claim link,
 * so the QR is never issued without it.
 */
export function GuestCheckoutIdleStep({
  amount,
  email,
  emailError,
  error,
  isStarting,
  startingLabel,
  onEmailChange,
  onSubmit,
}: GuestCheckoutIdleStepProps) {
  const idr = new Intl.NumberFormat("id-ID");
  const rp = (n: number) => `Rp${idr.format(n)}`;
  const emailDescribedBy = emailError
    ? "guest-checkout-email-hint guest-checkout-email-error"
    : "guest-checkout-email-hint";

  return (
    <form
      className="space-y-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {error ? (
        <p className="text-sm text-status-error-text" role="alert">
          {error}
        </p>
      ) : null}
      <div className="rounded-2xl border border-border-primary bg-card p-5 text-center">
        <p className="text-3xl font-bold text-text-heading">{rp(amount)}</p>
        <p className="mt-1 text-sm text-text-muted">
          Sekali bayar, langsung dapat akses penuh.
        </p>
      </div>
      <div className="space-y-1.5">
        <label
          htmlFor="guest-checkout-email"
          className="text-sm font-medium text-text-heading"
        >
          Email kamu
        </label>
        <Input
          id="guest-checkout-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          placeholder="nama@email.com"
          aria-invalid={emailError ? true : undefined}
          aria-describedby={emailDescribedBy}
          className="h-12"
        />
        <p id="guest-checkout-email-hint" className="text-xs text-text-muted">
          Bukti pembayaran dan link klaim bundle dikirim ke email ini.
        </p>
        {emailError ? (
          <p
            id="guest-checkout-email-error"
            className="text-sm text-status-error-text"
            role="alert"
          >
            {emailError}
          </p>
        ) : null}
      </div>
      <Button
        type="submit"
        disabled={isStarting}
        className="min-h-12 w-full gap-2 bg-brand-primary text-white hover:bg-brand-primary-hover"
      >
        {isStarting ? (
          <>
            <Loader2
              className="h-4 w-4 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
            {startingLabel}
          </>
        ) : (
          `Ambil Career OS Bundle ${rp(amount)}`
        )}
      </Button>
      <p className="text-center text-xs text-text-muted">
        Bayar pakai QRIS, diamankan Singapay.
      </p>
    </form>
  );
}
